import * as THREE from 'three';

/**
 * Creates enhanced ASCII texture with glow effects
 * @param {string} char - ASCII character to render
 * @param {number} color - Hex color value
 * @param {number} size - Canvas size (default 128)
 * @returns {THREE.CanvasTexture} Generated texture
 */
export function createASCIITexture(char, color, size = 128) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, size, size);
    
    // Enhanced font rendering
    ctx.font = `bold ${size * 0.7}px 'JetBrains Mono', monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Multi-layer glow effect
    const r = (color >> 16) & 255;
    const g = (color >> 8) & 255;
    const b = color & 255;
    
    // Outer glow
    ctx.shadowColor = `rgb(${r}, ${g}, ${b})`;
    ctx.shadowBlur = 12;
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.8)`;
    ctx.fillText(char, size / 2, size / 2);
    
    // Inner glow
    ctx.shadowBlur = 6;
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.fillText(char, size / 2, size / 2);
    
    // Core
    ctx.shadowBlur = 0;
    ctx.fillStyle = `rgb(${Math.min(255, r + 50)}, ${Math.min(255, g + 50)}, ${Math.min(255, b + 50)})`;
    ctx.fillText(char, size / 2, size / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
}

/**
 * Creates a material for ASCII entities
 * @param {THREE.Texture} texture - ASCII texture
 * @param {number} opacity - Material opacity (default 1)
 * @returns {THREE.MeshBasicMaterial} Configured material
 */
export function createASCIIMaterial(texture, opacity = 1) {
    return new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        alphaTest: 0.1,
        side: THREE.DoubleSide,
        opacity: opacity
    });
}

/**
 * Utility to convert hex color to RGB object
 * @param {number} hex - Hex color value
 * @returns {{r: number, g: number, b: number}} RGB values (0-255)
 */
export function hexToRgb(hex) {
    return {
        r: (hex >> 16) & 255,
        g: (hex >> 8) & 255,
        b: hex & 255
    };
}

/**
 * Creates animated ASCII texture with pulsing effect
 * @param {string} char - ASCII character
 * @param {number} color - Hex color value
 * @param {number} time - Animation time
 * @returns {THREE.CanvasTexture} Animated texture
 */
export function createAnimatedASCIITexture(char, color, time) {
    const canvas = document.createElement('canvas');
    const size = 128;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, size, size);
    
    const pulse = 0.8 + 0.2 * Math.sin(time * 4);
    const fontSize = size * 0.7 * pulse;
    
    ctx.font = `bold ${fontSize}px 'JetBrains Mono', monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const {r, g, b} = hexToRgb(color);
    const intensity = 0.8 + 0.2 * Math.sin(time * 2);
    
    ctx.shadowColor = `rgb(${r}, ${g}, ${b})`;
    ctx.shadowBlur = 15 * intensity;
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${intensity})`;
    ctx.fillText(char, size / 2, size / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
} 